import { createFileRoute } from '@tanstack/react-router'
import { Fragment, useState } from 'react'
import { ShieldCheck, Plus, Trash2 } from 'lucide-react'
import {
  usePermissionMatrix,
  useUpdatePermissionCell,
  useRoleRegistry,
  useCreateRole,
  useDeleteRole,
} from '@/hooks/data/usePermissionMatrix'
import { toast } from '@/ui/Toast'
import { userMessage } from '@/api/errors'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import type { PermissionCell } from '@/types/domain'

const ROLE_LABELS: Record<string, string> = { customer: 'Customer', manager: 'Manager', worker: 'Worker', admin: 'Admin' }

/** Modules in the order (and under the headings) of the admin sidebar, with the sidebar's own names. Anything the backend adds that isn't
 * listed here still shows up — under "Other", by its raw name — so a new module can never be hidden from this page. */
const SECTIONS: { title: string; modules: [string, string][] }[] = [
  { title: 'Overview', modules: [['Dashboard', 'Dashboard'], ['EcommerceAnalytics', 'Ecommerce Analytics']] },
  { title: 'Sales & Support', modules: [['Orders', 'Orders'], ['ContactRequests', 'Contact Requests'], ['Messages', 'Messages'], ['Notifications', 'Notifications'], ['NotificationTemplates', 'Notification Templates']] },
  { title: 'Catalog', modules: [['Products', 'Products'], ['Categories', 'Categories'], ['Inventory', 'Inventory']] },
  {
    title: 'Marketing',
    modules: [
      ['HeroSlides', 'Hero Slides'], ['PromoTiles', 'Promo Tiles'], ['Campaigns', 'Campaigns & Offers'], ['Discounts', 'Discount Rules'],
      ['LoyaltyTiers', 'Loyalty Program — Tiers'], ['LoyaltyMembers', 'Loyalty Program — Members'], ['Referrals', 'Referrals'],
      ['ReviewModeration', 'Reviews'], ['Newsletter', 'Newsletter'],
    ],
  },
  { title: 'Store Setup', modules: [['Locations', 'Locations & Shipping']] },
  { title: 'Finance', modules: [['Billing', 'Billing'], ['Payments', 'Payments']] },
  { title: 'Website', modules: [['WebsiteCms', 'Website (CMS)'], ['SiteSettings', 'Site Settings'], ['Testimonials', 'Testimonials'], ['Blog', 'Blog']] },
]
const MODULE_LABEL = new Map(SECTIONS.flatMap((s) => s.modules))
const MODULE_ORDER = new Map(SECTIONS.flatMap((s) => s.modules).map(([key], i) => [key, i]))
const sectionOf = (module: string) => SECTIONS.find((s) => s.modules.some(([key]) => key === module))?.title ?? 'Other'

function groupRows(cells: PermissionCell[]) {
  const byKey = new Map<string, { module: string; action: string; cells: PermissionCell[] }>()
  for (const c of cells) {
    const key = `${c.module}/${c.action}`
    if (!byKey.has(key)) byKey.set(key, { module: c.module, action: c.action, cells: [] })
    byKey.get(key)!.cells.push(c)
  }
  return Array.from(byKey.values()).sort(
    (a, b) => (MODULE_ORDER.get(a.module) ?? 999) - (MODULE_ORDER.get(b.module) ?? 999),
  )
}

function RolesSection() {
  const roles = useRoleRegistry()
  const createRole = useCreateRole()
  const deleteRole = useDeleteRole()
  const confirmDelete = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const submit = async () => {
    try {
      await createRole.mutateAsync({ name: name.trim(), description: description.trim() || undefined })
      toast.success(`Role "${name.trim()}" created — grant it access below.`)
      setName('')
      setDescription('')
      setCreating(false)
    } catch (err) {
      toast.error(userMessage(err))
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Roles</h2>
        <button
          onClick={() => setCreating((v) => !v)}
          className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white"
        >
          <Plus className="h-4 w-4" /> New role
        </button>
      </div>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Built-in roles (admin/manager/worker/customer) can't be deleted. A new custom role starts with zero access — grant it specific cells in the matrix below.
      </p>

      {creating && (
        <div className="mb-4 flex flex-wrap items-end gap-2 rounded-lg border border-gray-100 p-3 dark:border-gray-700">
          <div>
            <label className="text-xs text-gray-500">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="content-editor"
              className="mt-1 block rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-500">Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
              className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <button
            onClick={submit}
            disabled={!name.trim() || createRole.isPending}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
          >
            Create
          </button>
        </div>
      )}

      {roles.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <ul className="space-y-1.5">
          {roles.data?.map((r) => (
            <li key={r.name} className="flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-gray-800 dark:text-gray-100">{r.name}</span>
                {r.isBuiltIn && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                    built-in
                  </span>
                )}
                {r.description && <span className="text-xs text-gray-400">{r.description}</span>}
              </div>
              {!r.isBuiltIn && r.id && (
                <button
                  onClick={() =>
                    confirmDelete(r.name, async () => {
                      try {
                        await deleteRole.mutateAsync(r.id!)
                      } catch (err) {
                        toast.error(userMessage(err))
                      }
                    })
                  }
                  className="p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                  title="Delete role"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function PermissionsPage() {
  const matrix = usePermissionMatrix()
  const update = useUpdatePermissionCell()

  const rows = matrix.data ? groupRows(matrix.data) : []
  const allRoles = Array.from(new Set(rows.flatMap((r) => r.cells.map((c) => c.role))))

  const toggle = async (cell: PermissionCell) => {
    try {
      await update.mutateAsync({ module: cell.module, action: cell.action, role: cell.role, allowed: !cell.allowed })
    } catch (err) {
      toast.error(userMessage(err))
    }
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" /> Permissions
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          A real, enforced permission layer on top of the platform's role-based access control, covering every module in the admin sidebar — Dashboard through Blog — each with its real actions (View, Create, Edit, Delete, plus Cancel / Reply / Send / Refund where they exist). For built-in roles (admin/manager/worker/customer), toggling a cell off restricts access they have by default. For custom roles, a cell starts unchecked (zero access) — toggling it on grants that role real capability it has no code-level entitlement to. Enforced at the gateway and inside each service within ~30s of a change.
        </p>
      </div>

      <RolesSection />

      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        {matrix.isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : matrix.isError ? (
          <p className="text-sm text-red-600">Failed to load — this page requires the admin role.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="pb-2 pr-4">Module</th>
                  <th className="pb-2 pr-4">Action</th>
                  {allRoles.map((r) => (
                    <th key={r} className="pb-2 px-4 text-center whitespace-nowrap">{ROLE_LABELS[r] ?? r}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((row, idx) => (
                  <Fragment key={`${row.module}/${row.action}`}>
                    {(idx === 0 || sectionOf(rows[idx - 1].module) !== sectionOf(row.module)) && (
                      <tr className="bg-gray-50 dark:bg-gray-900/40">
                        <td colSpan={2 + allRoles.length} className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {sectionOf(row.module)}
                        </td>
                      </tr>
                    )}
                  <tr>
                    <td className="py-2.5 pr-4 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {rows[idx - 1]?.module === row.module ? '' : (MODULE_LABEL.get(row.module) ?? row.module)}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-300 whitespace-nowrap">{row.action}</td>
                    {allRoles.map((r) => {
                      const cell = row.cells.find((c) => c.role === r)
                      if (!cell) return <td key={r} className="py-2.5 px-4 text-center text-gray-300">—</td>
                      return (
                        <td key={r} className="py-2.5 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={cell.allowed}
                            onChange={() => toggle(cell)}
                            disabled={update.isPending}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                          />
                        </td>
                      )
                    })}
                  </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/permissions/')({ component: PermissionsPage })
